// The run cockpit's instruments, drawn as the soma harness draws them: loss with fast and
// slow averages and the best level in orange; the residual per band with its baseline,
// excess and the controller's target band; the text just read, lit by surprise; and the
// gain the model gives each band, around an even split.

export const LIVE = 'rgb(77, 255, 133)';
export const SIGNAL = 'rgb(255, 158, 66)';
export const DANGER = 'rgb(255, 92, 92)';

function average(values, alpha) {
  const out = [];
  let level = values[0] ?? 0;
  for (const v of values) { level += alpha * (v - level); out.push(level); }
  return out;
}

export function Panel({ title, note, children }) {
  return (
    <section className="min-w-0">
      <div className="flex items-baseline gap-x-3 gap-y-0.5 flex-wrap mb-3 border-b border-[#1e251f] pb-2">
        <h2 className="text-white font-mono text-sm lowercase whitespace-nowrap">{title}</h2>
        {note && <span className="text-[#555555] font-mono text-xs lowercase">{note}</span>}
      </div>
      {children}
    </section>
  );
}

export function LossChart({ values, height = 100 }) {
  const W = 600, H = height;
  if (values.length < 2) return <div style={{ height }} className="text-[#555555] font-mono text-xs flex items-center">waiting for the first points</div>;
  const fast = average(values, 0.12), slow = average(values, 0.02);
  const settled = slow.slice(Math.min(Math.floor(slow.length / 5), 40));
  const best = settled.length ? Math.min(...settled) : null;
  const lower = Math.min(...values), upper = Math.max(...values), span = Math.max(1e-9, upper - lower);
  const y = (v) => 3 + (H - 6) * (1 - (v - lower) / span);
  const path = (series) => series.map((v, i) => `${i ? 'L' : 'M'}${(W * i / (series.length - 1)).toFixed(1)},${y(v).toFixed(1)}`).join('');
  return (
    <div className="flex gap-3 items-stretch" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="flex-1 min-w-0" style={{ height }}>
        {best !== null && <line x1="0" x2={W} y1={y(best)} y2={y(best)} stroke={SIGNAL} strokeOpacity="0.75" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />}
        <path d={path(values)} fill="none" stroke={LIVE} strokeOpacity="0.18" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <path d={path(fast)} fill="none" stroke={LIVE} strokeOpacity="0.5" strokeWidth="1.1" vectorEffect="non-scaling-stroke" />
        <path d={path(slow)} fill="none" stroke={LIVE} strokeOpacity="0.3" strokeWidth="4" vectorEffect="non-scaling-stroke" />
        <path d={path(slow)} fill="none" stroke={LIVE} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex flex-col justify-between text-right font-mono text-[10px] text-[#666666] shrink-0">
        <span>{upper.toFixed(3)}</span>
        {best !== null && <span style={{ color: SIGNAL, opacity: 0.9 }}>best {best.toFixed(3)}</span>}
        <span>{lower.toFixed(3)}</span>
      </div>
    </div>
  );
}

export function ResidualBars({ values, baseline = [], excess = [], centre = null, height = 100 }) {
  const n = values.length;
  if (!n) return <div style={{ height }} className="text-[#555555] font-mono text-xs flex items-center">waiting for a report</div>;
  const W = 600, H = height - 14, gap = 2;
  const maximum = Math.max(0.1, ...values, ...baseline, ...excess);
  const width = Math.max(1, (W - gap * (n - 1)) / n);
  const y = (v) => H * (1 - Math.max(0, v) / maximum);
  const cx = centre !== null && n > 1 ? Math.min(W, Math.max(0, centre / (n - 1) * W)) : null;
  return (
    <div style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: H }}>
        {values.map((v, i) => {
          const x = i * (width + gap);
          const core = Math.max(1, width * 0.3);
          return (
            <g key={i}>
              <rect x={x} y={y(v)} width={width} height={H - y(v)} fill={LIVE} fillOpacity="0.32" />
              {excess[i] > 0 && <rect x={x + (width - core) / 2} y={y(excess[i])} width={core} height={H - y(excess[i])} fill={LIVE} />}
              {i < baseline.length && <rect x={x} y={y(baseline[i]) - 0.75} width={width} height="1.5" fill="white" fillOpacity="0.55" />}
            </g>
          );
        })}
        {cx !== null && <line x1={cx} x2={cx} y1="0" y2={H} stroke={SIGNAL} strokeWidth="1.2" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="flex justify-between font-mono text-[9px] text-[#555555] mt-0.5"><span>fast</span><span>slow</span></div>
    </div>
  );
}

// the tokenizer spells some characters as byte pieces (<0x60>); show the printable ones
const piece = (text) => text.replace(/<0x([0-9A-Fa-f]{2})>/g, (whole, hex) => {
  const code = parseInt(hex, 16);
  return code === 10 ? '\n' : code >= 32 && code < 127 ? String.fromCharCode(code) : whole;
});

export function SurpriseText({ tokens, height = 150 }) {
  const scored = tokens.map(t => t[1]).filter(b => b !== null && b !== undefined).sort((a, b) => a - b);
  const flare = scored.length ? scored[Math.min(scored.length - 1, Math.floor(scored.length * 0.92))] : Infinity;
  const colour = ([text, bits]) => {
    if (bits === null || bits === undefined) return 'rgba(255,255,255,0.2)';
    if (text.startsWith('[')) return 'rgba(255,255,255,0.36)';
    if (bits >= flare) return LIVE;
    return `rgba(255,255,255,${0.26 + 0.7 * Math.min(1, bits / 12)})`;
  };
  return (
    <div className="relative overflow-hidden flex flex-col justify-end" style={{ height,
      maskImage: 'linear-gradient(to bottom, transparent, black 25%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 25%)' }}>
      <p className="font-mono text-xs leading-5 whitespace-pre-wrap break-words">
        {tokens.length ? tokens.map((t, i) => <span key={i} style={{ color: colour(t) }}>{piece(t[0])}</span>)
          : <span className="text-[#555555]">waiting for a report</span>}
      </p>
    </div>
  );
}

export function listeningCentre(shares) {
  const total = (shares || []).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return shares.reduce((acc, s, i) => acc + i * s, 0) / total;
}

export function ListeningBars({ now = [], start = null, height = 150 }) {
  const n = now.length;
  if (!n) return <div style={{ height }} className="text-[#555555] font-mono text-xs flex items-center">waiting for a report</div>;
  const W = 600, H = height - 14, gap = 2, middle = H / 2;
  const relative = now.map(v => v * n);
  const origin = (start || []).map(v => v * n);
  const spread = Math.max(0.08, ...[...relative, ...origin].map(v => Math.abs(v - 1)));
  const width = Math.max(1, (W - gap * (n - 1)) / n);
  const offset = (v) => -((v - 1) / spread) * (H / 2 - 4);
  return (
    <div style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: H }}>
        {relative.map((v, i) => {
          const x = i * (width + gap), dy = offset(v);
          return (
            <g key={i}>
              <rect x={x} y={Math.min(middle, middle + dy)} width={width} height={Math.max(1, Math.abs(dy))}
                fill={dy < 0 ? LIVE : 'white'} fillOpacity={dy < 0 ? 0.75 : 0.28} />
              {i < origin.length && <rect x={x} y={middle + offset(origin[i]) - 0.75} width={width} height="1.5" fill="white" fillOpacity="0.6" />}
            </g>
          );
        })}
        <line x1="0" x2={W} y1={middle} y2={middle} stroke="white" strokeOpacity="0.35" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between font-mono text-[9px] text-[#555555] mt-0.5">
        <span>fast</span><span className="hidden sm:inline">above the line: amplified · ticks: run start</span><span>slow</span>
      </div>
    </div>
  );
}

export function formatBytes(n) {
  if (!n) return '0';
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)} TB`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} kB`;
  return `${n} B`;
}

export function formatCount(n) {
  if (n === null || n === undefined) return '–';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${Math.round(n)}`;
}

export function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export const LIVE_STATUSES = ['requested', 'provisioning', 'preparing', 'training', 'saving', 'paused'];

export function StatusChip({ status }) {
  const live = LIVE_STATUSES.includes(status);
  const bad = ['failed', 'lost'].includes(status);
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] lowercase px-2 h-5 rounded-full border"
      style={{ borderColor: bad ? 'rgba(255,92,92,0.45)' : live ? 'rgba(77,255,133,0.45)' : '#383f38',
               color: bad ? DANGER : live ? LIVE : '#888888' }}>
      {live && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: LIVE }} />}
      {status}
    </span>
  );
}
